import React from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import { useSyncedTheme } from '../../hooks/useSyncedTheme';
import { toggleAppTheme } from '../../utils/themeUtils';

const ACCENT = '#4ce1b6';

/**
 * Compact theme toggle — sun/moon icon pill for web, mobile, and tablet headers.
 */
export default function ThemeToggleButton({
  compact = false,
  sx,
}) {
  const isDark = useSyncedTheme() === 'dark';
  const buttonSize = compact ? 34 : 38;
  const iconSize = compact ? 18 : 20;

  return (
    <Tooltip
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      arrow
      placement="bottom"
    >
      <IconButton
        onClick={toggleAppTheme}
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        size="small"
        sx={{
          width: buttonSize,
          height: buttonSize,
          p: 0,
          borderRadius: '11px',
          border: `1px solid ${isDark ? alpha('#ffffff', 0.1) : alpha('#0f172a', 0.12)}`,
          background: isDark
            ? `linear-gradient(145deg, ${alpha('#ffffff', 0.07)} 0%, ${alpha('#000000', 0.35)} 100%)`
            : `linear-gradient(145deg, ${alpha('#ffffff', 0.95)} 0%, ${alpha(ACCENT, 0.08)} 100%)`,
          color: isDark ? '#fbbf24' : '#6366f1',
          boxShadow: isDark
            ? `inset 0 1px 0 ${alpha('#ffffff', 0.06)}, 0 4px 14px ${alpha('#000000', 0.35)}`
            : `inset 0 1px 0 ${alpha('#ffffff', 0.8)}, 0 4px 12px ${alpha(ACCENT, 0.15)}`,
          transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
          '&:hover': {
            borderColor: alpha(ACCENT, 0.45),
            boxShadow: `0 0 0 3px ${alpha(ACCENT, 0.14)}, 0 6px 16px ${alpha(ACCENT, 0.18)}`,
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'scale(0.94)',
          },
          ...sx,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {isDark ? (
            <LightModeOutlinedIcon sx={{ fontSize: iconSize }} />
          ) : (
            <DarkModeOutlinedIcon sx={{ fontSize: iconSize }} />
          )}
        </Box>
      </IconButton>
    </Tooltip>
  );
}
